<template>
  <!-- content-wrapper/content, not a bare .report-page: DefaultLayout renders a
       FIXED navbar and sidebar and then the router-view with no offset of its
       own, so a route component that omits .content-wrapper (src/theme/Base.scss)
       paints from 0,0 — under the navbar and behind the sidebar. The tier reports
       do not hit this because they are mounted inside TierReport.vue, which
       carries the wrapper; this report IS the route component. -->
  <div class="content-wrapper view-voucher-batch-report p-0">
    <div class="content">
      <div class="report-page">
        <div class="card">
          <div class="card-body">
            <b-alert :show="!!errorMessage" variant="danger">{{ errorMessage }}</b-alert>

            <div v-if="isLoading && !batch" class="text-center text-muted py-4">
              <b-spinner small class="mr-2"></b-spinner>กำลังโหลดรายงาน...
            </div>

            <div v-if="batch" class="batch-header d-flex align-items-center flex-wrap mb-3">
              <h4 class="mb-0 mr-2">{{ batch.name }}</h4>
              <b-badge v-if="isExpired" variant="danger" class="mr-2">หมดอายุแล้ว</b-badge>
              <span class="text-muted mr-3">คำนำหน้ารหัส {{ batch.codePrefix }}</span>
              <span class="text-muted">{{ validityLine }}</span>
            </div>

            <div v-if="summary" class="summary-tiles d-flex flex-wrap mb-3">
              <div class="summary-tile">
                <div class="summary-tile-label">ออก</div>
                <div class="summary-tile-value">{{ summary.issued }}</div>
              </div>
              <div class="summary-tile">
                <div class="summary-tile-label">ใช้แล้ว</div>
                <div class="summary-tile-value">{{ summary.redeemed }}</div>
              </div>
              <div class="summary-tile">
                <div class="summary-tile-label">คงเหลือ</div>
                <div class="summary-tile-value">{{ summary.remaining }}</div>
              </div>
              <div class="summary-tile">
                <div class="summary-tile-label">หมดอายุ</div>
                <div class="summary-tile-value">{{ summary.expired }}</div>
              </div>
              <div class="summary-tile">
                <div class="summary-tile-label">ยกเลิก</div>
                <div class="summary-tile-value">{{ summary.voided }}</div>
              </div>
              <div class="summary-tile">
                <div class="summary-tile-label">อัตราการใช้</div>
                <div class="summary-tile-value">{{ summary.rate }}%</div>
              </div>
              <div class="summary-tile">
                <div class="summary-tile-label">ยอดส่วนลดรวม</div>
                <div class="summary-tile-value">{{ summary.totalDiscount | comma }} บาท</div>
              </div>
            </div>

            <template v-if="batch">
              <h5 class="mb-2">ยอดส่วนลดแยกตามสาขา</h5>
              <table class="table table-sm table-bordered mb-4">
                <thead class="thead-light">
                  <tr>
                    <th>สาขา</th>
                    <th class="text-right">ยอดส่วนลด (บาท)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!byBranch.length">
                    <td colspan="2" class="text-center text-muted">ยังไม่มีการใช้บัตรในชุดนี้</td>
                  </tr>
                  <tr v-for="row in byBranch" v-else :key="row.branchId">
                    <td>{{ row.branchName }}</td>
                    <td class="text-right">{{ row.discount | comma }}</td>
                  </tr>
                </tbody>
              </table>

              <div class="toolbar d-flex align-items-center flex-wrap mb-2">
                <div class="field mr-3">
                  <label class="field-lbl">กรองตามสถานะ</label>
                  <b-form-select
                    v-model="statusFilter"
                    :options="statusOptions"
                    size="sm"
                    class="field-input-native"
                  ></b-form-select>
                </div>
                <div class="spacer flex-grow-1"></div>
                <b-dropdown text="ส่งออก" size="sm" variant="outline-success" right :disabled="!codes.length">
                  <b-dropdown-item @click="exportExcel">
                    <i class="fas fa-file-excel mr-2"></i>Excel
                  </b-dropdown-item>
                  <b-dropdown-item @click="exportPdf">
                    <i class="fas fa-file-pdf mr-2"></i>PDF
                  </b-dropdown-item>
                </b-dropdown>
              </div>

              <b-table
                id="voucherBatchReportTable"
                :items="codes"
                :fields="fields"
                :busy="isLoading"
                responsive
                hover
                show-empty
                empty-text="ไม่มีข้อมูลให้แสดง"
              >
                <template #cell(status)="{ item }">{{ statusLabel(item.status) }}</template>
                <template #cell(redeemedDt)="{ value }">{{ fmtDate(value) }}</template>
                <template #cell(patientHn)="{ value }">{{ value || "—" }}</template>
                <template #cell(patientName)="{ value }">{{ value || "—" }}</template>
                <template #cell(branchName)="{ value }">{{ value || "—" }}</template>
                <template #cell(redeemedByName)="{ value }">{{ value || "—" }}</template>
              </b-table>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import moment from "moment";
import { exportTableToExcel, exportReportToPdf } from "@/utils/reportExport";

export default {
  name: "VoucherBatchReport",
  data() {
    return {
      isLoading: false,
      errorMessage: "",
      statusFilter: null,
      report: null,
      fields: [
        { key: "code", label: "รหัสบัตร" },
        { key: "status", label: "สถานะ" },
        { key: "redeemedDt", label: "วันที่ใช้" },
        { key: "patientHn", label: "HN" },
        { key: "patientName", label: "ชื่อคนไข้" },
        { key: "branchName", label: "สาขาที่ใช้" },
        { key: "redeemedByName", label: "พนักงานที่ตัดใช้" },
      ],
      statusOptions: [
        { value: null, text: "ทุกสถานะ" },
        { value: "UNUSED", text: "ยังไม่ใช้" },
        { value: "REDEEMED", text: "ใช้แล้ว" },
        { value: "VOID", text: "ยกเลิก" },
      ],
      reportTitle: "รายงานบัตรกำนัล",
      reportNameEn: "VoucherBatch",
    };
  },
  computed: {
    ...mapGetters({
      branchInfo: "moduleBranch/getBranchInfo",
      clinicInfo: "moduleBranch/getClinicInfo",
    }),
    batch() { return this.report?.batch || null; },
    codes() { return this.report?.codes || []; },
    // Expiry is derived from the batch, never stored per code — see the note above.
    isExpired() {
      if (!this.batch?.validTo) return false;
      return moment(this.batch.validTo).endOf("day").isBefore(moment());
    },
    summary() {
      const b = this.batch;
      if (!b) return null;
      const unused = b.remaining || 0;
      return {
        issued: b.issued || 0,
        redeemed: b.redeemed || 0,
        remaining: this.isExpired ? 0 : unused,
        expired: this.isExpired ? unused : 0,
        voided: b.voided || 0,
        rate: b.redemptionRate || 0,
        totalDiscount: b.totalDiscount || 0,
      };
    },
    byBranch() { return this.batch?.discountByBranch || []; },
    // "Never expires" is decided by validTo alone — validFrom and validTo are
    // independently optional on the batch, so all four combinations need a
    // distinct sentence (see fix-round-1 note).
    validityLine() {
      const b = this.batch;
      if (!b) return "";
      const { validFrom, validTo } = b;
      if (!validFrom && !validTo) return "ไม่มีวันหมดอายุ";
      if (validFrom && !validTo) return `เริ่ม ${this.fmtDate(validFrom)} · ไม่มีวันหมดอายุ`;
      if (!validFrom && validTo) return `ถึง ${this.fmtDate(validTo)}`;
      return `${this.fmtDate(validFrom)} – ${this.fmtDate(validTo)}`;
    },
  },
  methods: {
    ...mapActions("moduleVoucher", ["fetchBatchReport"]),

    statusLabel(s) {
      if (s === "UNUSED" && this.isExpired) return "หมดอายุ";
      return { UNUSED: "ยังไม่ใช้", REDEEMED: "ใช้แล้ว", VOID: "ยกเลิก" }[s] || s;
    },
    fmtDate(v) { return v ? moment(v).format("DD/MM/YYYY HH:mm") : "—"; },

    async load() {
      this.isLoading = true;
      this.errorMessage = "";
      try {
        this.report = await this.fetchBatchReport({
          clinicUrl: this.$route.params.clinicUrl,
          batchId: this.$route.params.batchId,
          status: this.statusFilter,
        });
      } catch (error) {
        this.errorMessage = error?.response?.data?.message || "โหลดรายงานไม่สำเร็จ";
      } finally {
        this.isLoading = false;
      }
    },


    // The shared helper reads the rendered table out of the DOM by id and adds
    // the standard DentCloud report header. The batch's own validity range
    // stands in for the date range every other report passes.
    exportOptions() {
      return {
        tableId: "voucherBatchReportTable",
        reportTitle: `${this.reportTitle} — ${this.batch ? this.batch.name : ""}`.trim(),
        reportNameEn: this.reportNameEn,
        dateFrom: this.batch && this.batch.validFrom,
        dateTo: this.batch && this.batch.validTo,
        branchName: this.branchInfo && this.branchInfo.name,
        clinicName: this.clinicInfo && this.clinicInfo.name,
      };
    },
    exportExcel() {
      exportTableToExcel(this.exportOptions());
    },
    exportPdf() {
      exportReportToPdf(this.exportOptions());
    },
  },
  watch: {
    statusFilter() { this.load(); },
  },
  created() { this.load(); },
};
</script>

<style scoped>
.summary-tiles {
  gap: 10px;
}
.summary-tile {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 8px 14px;
  min-width: 100px;
}
.summary-tile-label {
  font-size: 0.75rem;
  color: #6c757d;
}
.summary-tile-value {
  font-size: 1.15rem;
  font-weight: 700;
}
</style>
